import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { Lock, AlertCircle } from 'lucide-react';

interface LoginFormData {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'email' | 'phone'>('email');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginFormData>();

  const onSubmit = async ({ email, password }: LoginFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      await login(email, password);
      toast.success('Login successful');
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
      toast.error('Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center bg-gray-50">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl p-8 border border-gray-100">
        <div className="flex flex-col items-center">
          <span className="text-3xl font-bold text-blue-700 mb-2">CashBitAi</span>
          <h2 className="text-xl font-semibold text-gray-900 mb-6 text-center">
            Let's get you in to CashBitAi
          </h2>
        </div>

        {/* Login Type Toggle */}
        <div className="flex justify-center mb-2">
          <button
            className={`px-4 py-1 border-b-2 ${activeTab === 'email' ? 'border-green-600 text-black font-semibold' : 'border-transparent text-gray-400'}`}
            onClick={() => setActiveTab('email')}
            type="button"
          >
            Email
          </button>
          <button
            className={`px-4 py-1 border-b-2 ${activeTab === 'phone' ? 'border-green-600 text-black font-semibold' : 'border-transparent text-gray-400'}`}
            onClick={() => setActiveTab('phone')}
            type="button"
          >
            Phone
          </button>
        </div>

        {/* Login Form */}
        <form className="mt-4 space-y-4" onSubmit={handleSubmit(onSubmit)}>
          {activeTab === 'email' ? (
            <Input
              label="Email"
              placeholder="Enter your email"
              type="email"
              autoComplete="email"
              error={errors.email?.message}
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
            />
          ) : (
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                US Phone Number
              </label>
              <div className="flex items-center">
                <span className="inline-flex items-center px-2 py-2 border border-gray-300 bg-gray-50 rounded-l-md text-sm select-none">
                  🇺🇸 +1
                </span>
                <input
                  type="tel"
                  maxLength={10}
                  pattern="[0-9]{10}"
                  placeholder="1234567890"
                  className="border border-gray-300 rounded-r-md px-3 py-2 w-full focus:outline-none text-sm"
                  {...register('email', {
                    required: 'Phone number is required',
                    pattern: {
                      value: /^[0-9]{10}$/,
                      message: 'Enter a valid 10-digit US phone number'
                    }
                  })}
                />
              </div>
              {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email.message}</p>}
            </div>
          )}

          {/* Password Input */}
          <div className="relative">
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter your password"
              leftIcon={<Lock className="h-5 w-5 text-gray-400" />}
              error={errors.password?.message}
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 8,
                  message: 'Password must be at least 8 characters'
                }
              })}
              className="pr-10"
            />
            <button
              type="button"
              className="absolute right-3 top-7 text-green-700 text-xs flex items-center"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>

          {/* Forgot password link */}
          <div className="flex justify-end text-sm">
            <Link to="/forgot-password" className="text-blue-700 hover:underline">
              Forgot password?
            </Link>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 p-4 rounded-lg flex items-start">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            fullWidth
            isLoading={isLoading}
            className="bg-green-600 hover:bg-green-700 text-white flex items-center justify-center"
          >
            <svg
              className="h-5 w-5 mr-2"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <rect width="18" height="12" x="3" y="7" rx="2" stroke="currentColor" />
              <path d="M7 7V5a5 5 0 0 1 10 0v2" />
            </svg>
            Sign in
          </Button>
        </form>

        {/* Legal/Terms */}
        <p className="text-xs text-gray-500 mt-4 text-center">
          By signing in, you agree to our{' '}
          <a href="#" className="text-blue-600 underline">Terms</a> and{' '}
          <a href="#" className="text-blue-600 underline">Privacy Policy</a>.
        </p>

        {/* Register Link */}
        <div className="mt-4 text-center text-sm">
          New to CashBitAi?{' '}
          <Link to="/register" className="text-blue-700 underline">
            Create an account
          </Link>
        </div>

        {/* reCAPTCHA Notice */}
        <div className="mt-4 text-center">
          <span className="text-[10px] text-gray-400">
            Protected by reCAPTCHA.{' '}
            <a href="#" className="underline">Privacy</a> and{' '}
            <a href="#" className="underline">Terms</a> apply.
          </span>
        </div>
      </div>
    </div>
  );
};

export default Login;
